import { useCallback, useEffect, useMemo, useState } from 'react'
import { withStyles } from '@aura/components/styles'
import { Box, Button, Container, Grid, Typography, TextField, MenuItem, Select, FormControl, InputLabel, FormHelperText } from '@aura/components'
import { useTranslation } from 'react-i18next'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import PropTypes from 'prop-types'
import RegistrationContainer from '../../../../components/new-registration-container'
import CustomLoader from '../../../../components/custom-loader'
import styles from './styles'


const RECOVERY_TYPES = [
    { label: 'SMS', value: 'sms' },
    { label: 'Call', value: 'call' },
]

const getPhoneNumberError = (countryCode, phoneNumber, recoveryType) => {
    if (!phoneNumber || !countryCode) return null

    const phone = parsePhoneNumberFromString(countryCode + phoneNumber)
    if (!phone || !phone.isValid()) {
        return 'registrationFlow.phoneNumberInvalid'
    }

    if (recoveryType === 'sms') {
        const type = phone.getType()
        if (type !== 'MOBILE' && type !== 'FIXED_LINE_OR_MOBILE') {
            return 'registrationFlow.smsCapablePhoneRequired'
        }
    }

    return null
}

const Component = ({
    classes,
    countryCodeList,
    error,
    initialValues,
    isProcessing,
    onBack,
    onSubmit,
    userData
}) => {
    const { t: translate } = useTranslation()
    const [ recoveryType, setRecoveryType ] = useState(initialValues?.recoveryType || '')
    const [ countryCode, setCountryCode ] = useState(initialValues?.countryCode || '')
    const [ phoneNumber, setPhoneNumber ] = useState(initialValues?.phoneNumber || '')
    const [ extension, setExtension ] = useState(initialValues?.extension || '')
    const [ errors, setErrors ] = useState({})
    const [ apiErrorDismissed, setApiErrorDismissed ] = useState(false)

    // Reset dismissed flag whenever a new API error arrives
    useEffect(() => {
        if (error) setApiErrorDismissed(false)
    }, [ error ])

    const phoneNumberError = useMemo(
        () => getPhoneNumberError(countryCode, phoneNumber, recoveryType),
        [ countryCode, phoneNumber, recoveryType ]
    )

    useEffect(() => {
        setErrors(previousErrors => ({
            ...previousErrors,
            phoneNumber: phoneNumberError,
        }))
    }, [ phoneNumberError ])

    const title = translate('registrationFlow.phoneSetupTitle')

    const handlePhoneChange = useCallback(e => {
        const val = e.target.value.replace(/[^0-9]/g, '')
        setPhoneNumber(val)
        setApiErrorDismissed(true)
    }, [])

    const handleExtensionChange = useCallback(e => {
        const val = e.target.value.replace(/[^0-9]/g, '')
        setExtension(val)
    }, [])

    const handleRecoveryTypeChange = useCallback(e => {
        setRecoveryType(e.target.value)
        setErrors(prev => ({ ...prev, recoveryType: null }))
        // Clear extension when switching to SMS
        if (e.target.value === 'sms') {
            setExtension('')
        }
    }, [])

    const handleCountryCodeChange = useCallback(e => {
        setCountryCode(e.target.value)
        setErrors(prev => ({ ...prev, countryCode: null }))
        setApiErrorDismissed(true)
    }, [])

    const validate = useCallback(() => {
        const newErrors = {}

        if (!recoveryType) {
            newErrors.recoveryType = 'registrationFlow.recoveryTypeRequired'
        }
        if (!countryCode) {
            newErrors.countryCode = 'registrationFlow.countryCodeRequired'
        }
        if (!phoneNumber) {
            newErrors.phoneNumber = 'registrationFlow.phoneNumberRequired'
        } else if (phoneNumberError) {
            newErrors.phoneNumber = phoneNumberError
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }, [ recoveryType, countryCode, phoneNumber, phoneNumberError ])

    const isFormValid = Boolean(recoveryType && countryCode && phoneNumber && !phoneNumberError)

    const handleSubmit = useCallback(() => {
        if (!validate()) return

        onSubmit({
            oktaUserId: userData?.oktaUserId,
            recoveryCountryCode: countryCode,
            recoveryExtension: extension,
            recoveryPhoneNumber: phoneNumber,
            recoveryType,
        })
    }, [ recoveryType, countryCode, phoneNumber, extension, userData, onSubmit, validate ])

    const mainContent = useMemo(() => (
        isProcessing ? (
            <Container className={classes.customLoader}>
                <CustomLoader label={translate('loading')} />
            </Container>
        ) : <Box className={classes.contentWrapper}>
            <Box className={classes.content}>
                <Typography className={classes.subTitle}>
                    {translate('registrationFlow.phoneSetupDescription')}
                </Typography>

                {/* Fields Row: Recovery Type | Country Code | Phone Number | Extension (if call) */}
                <div className={classes.fieldsRow}>
                    {/* Recovery Type Dropdown */}
                    <FormControl
                        className={classes.recoveryTypeField}
                        variant='standard'
                        error={!!errors.recoveryType}
                    >
                        <InputLabel shrink>{translate('registrationFlow.recoveryType')}</InputLabel>
                        <Select
                            value={recoveryType}
                            onChange={handleRecoveryTypeChange}
                            displayEmpty
                            MenuProps={{
                                anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                                PaperProps: { style: { maxHeight: 200 } },
                                transformOrigin: { horizontal: 'left', vertical: 'top' },
                                
                            }}
                        >
                            {RECOVERY_TYPES.map(type => (
                                <MenuItem key={type.value} value={type.value}>
                                    {translate(`registrationFlow.recoveryTypeOption_${type.value}`)}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.recoveryType && <FormHelperText>{translate(errors.recoveryType)}</FormHelperText>}
                    </FormControl>

                    {/* Country Code Dropdown */}
                    <FormControl
                        className={classes.countryCodeField}
                        variant='standard'
                        error={!!errors.countryCode}
                    >
                        <InputLabel shrink>{translate('registrationFlow.countryCode')}</InputLabel>
                        <Select
                            value={countryCode}
                            onChange={handleCountryCodeChange}
                            displayEmpty
                            MenuProps={{
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                PaperProps: { style: { maxHeight: 200, overflowY: 'auto' } },
                            }}
                        >
                            {(countryCodeList || []).map(code => (
                                <MenuItem key={code.value} value={code.value}>
                                    {code.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.countryCode && <FormHelperText>{translate(errors.countryCode)}</FormHelperText>}
                    </FormControl>

                    {/* Phone Number Input */}
                    <TextField
                        className={classes.phoneNumberField}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber ? translate(errors.phoneNumber) : undefined}
                        inputProps={{ inputMode: 'numeric', maxLength: 15 }}
                        label={translate('registrationFlow.phoneNumber')}
                        onChange={handlePhoneChange}
                        value={phoneNumber}
                        variant='standard'
                        InputLabelProps={{ shrink: true }}
                    />

                    {/* Extension (only for voice call) */}
                    {recoveryType === 'call' && (
                        <TextField
                            className={classes.extensionField}
                            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                            label={translate('registrationFlow.extension')}
                            onChange={handleExtensionChange}
                            value={extension}
                            variant='standard'
                            InputLabelProps={{ shrink: true }}
                        />
                    )}
                </div>

                {/* Error from API - only show when no local validation errors (stale after user edits) */}
                {error && !apiErrorDismissed && !Object.values(errors).some(Boolean) && (
                    <Typography className={classes.apiError}>
                        {error}
                    </Typography>
                )}
            </Box>
            <div className={classes.helpText}>
                <p className={classes.subTitle}>{translate('registrationFlow.helpText')}</p>
            </div>
        </Box>
    ), [ classes, errors, apiErrorDismissed, recoveryType, countryCode, phoneNumber, extension, error, isProcessing, handleRecoveryTypeChange, handleCountryCodeChange, handlePhoneChange, handleExtensionChange, translate ])

    const footerContent = useMemo(() => (
        <Grid className={classes.buttonContainer}>
            <Button
                className={`${classes.nextButton} ${isProcessing || !isFormValid ? classes.nextButtonDisabled : ''}`}
                color='primary'
                disabled={isProcessing || !isFormValid}
                onClick={handleSubmit}
            >
                {translate('next')}
            </Button>
        </Grid>
    ), [ classes, isProcessing, isFormValid, handleSubmit, translate ])

    return (
        <RegistrationContainer {...{ footerActions: !isProcessing && footerContent, mainContent, title }}>
        </RegistrationContainer>
    )
}

Component.displayName = 'PhoneSetup'

Component.propTypes = {
    classes: PropTypes.object,
    countryCodeList: PropTypes.array,
    error: PropTypes.string,
    initialValues: PropTypes.object,
    isProcessing: PropTypes.bool,
    onBack: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    userData: PropTypes.object
}

export default withStyles(Component, styles)